import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { Grid, Stack, Box, Button, Tooltip, Typography } from '@mui/material'
import { Add as AddIcon, Remove as RemoveIcon } from '@mui/icons-material'
import WorkerRequiredButton from '../../../common/WorkerRequiredButton'
import { useSmartTranslation } from '../../../../../generic/hooks/useSmartTranslation'
import NumericKeypad from '../../../common/NumericKeypad'

/**
 * JobControls Component
 * Handles the counter and reporting actions (OK/NOK).
 *
 * @param {Object} props
 * @param {number} props.counter
 * @param {function} props.setCounter
 * @param {function} props.handleIncrement
 * @param {function} props.handleDecrement
 * @param {function} props.onReportOk
 * @param {function} props.onReportNok
 * @param {boolean} props.workerPresent
 * @param {boolean} [props.reportDisabled] S79 (story 100, RN34): true for an external-of-the-circuit
 *        job -- Report (OK/NOK) is NOT gated by the acceptance form (#86 never touched it), so this is its
 *        own flag, on top of (not instead of) `controlsDisabled`. Story 113 (S134) adds the second reason:
 *        the job is in preparation.
 *
 *        ⚠️ Story 113 EXTENDS its reach to the quantity counter and its keypad, which it did not gate
 *        before: S134 lists them among the controls that must come out disabled in preparation, and
 *        letting the operator type a quantity that can never be reported is a dead end. Declared side
 *        effect: the counter is now gated for an external-circuit job too, which is consistent with
 *        S79 (its Report was already blocked) but is a change to that story's surface.
 * @param {string} [props.reportDisabledTooltip] why Report is disabled, empty when it does not apply --
 *        MUI renders no tooltip for an empty title.
 */
const JobControls = ({
  counter,
  setCounter,
  handleIncrement,
  handleDecrement,
  onReportOk,
  onReportNok,
  workerPresent,
  controlsDisabled,
  reportDisabled = false,
  reportDisabledTooltip = ''
}) => {
  const { t_ } = useSmartTranslation()
  const [keypadOpen, setKeypadOpen] = useState(false)

  // S134: one gate for every quantity control of this widget, so the counter, the keypad and the two
  // report actions can never disagree about whether reporting is possible.
  const quantityDisabled = controlsDisabled || reportDisabled

  const handleOpenKeypad = () => {
    if (workerPresent && !quantityDisabled) {
      setKeypadOpen(true)
    }
  }

  const handleAcceptKeypad = (val) => {
    if (setCounter) {
      setCounter(val)
    }
  }

  return (
    <Grid item xs={12} md={5} lg={4}>
      <Stack spacing={2}>
        {/* S134: the counter keeps its place in the layout while disabled (S135: nothing moves when
            the mode changes) and carries the same reason as the two report actions. Same wrapping
            pattern as the disabled finish button: a disabled control fires no mouse events. */}
        <Tooltip title={reportDisabledTooltip} placement='top' arrow>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Button
              variant='contained'
              color='inherit'
              sx={{ minWidth: 50, width: 50, height: 50, bgcolor: 'background.default' }}
              onClick={handleDecrement}
              disabled={!workerPresent || quantityDisabled}
            >
              <RemoveIcon fontSize='large' />
            </Button>
            <Box
              onClick={handleOpenKeypad}
              sx={{
                bgcolor: 'background.default',
                px: 2,
                py: 1.5,
                borderRadius: 1,
                minWidth: 70,
                textAlign: 'center',
                cursor: workerPresent && !quantityDisabled ? 'pointer' : 'default',
                '&:hover': {
                  bgcolor: workerPresent && !quantityDisabled ? 'action.hover' : 'background.default'
                }
              }}
            >
              <Typography variant='h5'>{counter}</Typography>
            </Box>
            <Button
              variant='contained'
              color='inherit'
              sx={{ minWidth: 50, width: 50, height: 50, bgcolor: 'background.default' }}
              onClick={handleIncrement}
              disabled={!workerPresent || quantityDisabled}
            >
              <AddIcon fontSize='large' />
            </Button>
          </Box>
        </Tooltip>
        <Stack direction='row' spacing={1}>
          {/* S79 (story 100): the Tooltip wraps a Box because a disabled MUI button fires no mouse
              events, same wrapping JobHeader (S62) and the assembly area (S61) already use. */}
          <Tooltip title={reportDisabledTooltip} placement='top' arrow>
            <Box sx={{ width: '100%' }}>
              <WorkerRequiredButton
                variant='outlined'
                color='success'
                fullWidth
                sx={{ height: 50, fontSize: '1.2rem' }}
                onClick={() => onReportOk && onReportOk(counter)}
                disabled={controlsDisabled || reportDisabled}
              >
                {t_('OK')}
              </WorkerRequiredButton>
            </Box>
          </Tooltip>
          <Tooltip title={reportDisabledTooltip} placement='top' arrow>
            <Box sx={{ width: '100%' }}>
              <WorkerRequiredButton
                variant='outlined'
                color='error'
                fullWidth
                sx={{ height: 50, fontSize: '1.2rem' }}
                onClick={() => onReportNok && onReportNok(counter)}
                disabled={controlsDisabled || reportDisabled}
              >
                {t_('NOK')}
              </WorkerRequiredButton>
            </Box>
          </Tooltip>
        </Stack>
      </Stack>

      <NumericKeypad
        open={keypadOpen}
        onClose={() => setKeypadOpen(false)}
        onAccept={handleAcceptKeypad}
        initialValue={counter}
        title={t_('Enter Quantity')}
        allowDecimals={false}
      />
    </Grid>
  )
}

JobControls.propTypes = {
  counter: PropTypes.number.isRequired,
  setCounter: PropTypes.func,
  handleIncrement: PropTypes.func.isRequired,
  handleDecrement: PropTypes.func.isRequired,
  onReportOk: PropTypes.func,
  onReportNok: PropTypes.func,
  workerPresent: PropTypes.oneOfType([PropTypes.object, PropTypes.bool]),
  controlsDisabled: PropTypes.bool,
  reportDisabled: PropTypes.bool,
  reportDisabledTooltip: PropTypes.string
}

export default JobControls
